import React, { useCallback } from 'react';
import CypAdhdReviewDocument from './CypAdhdReviewDocument';

/**
 * RD_PAD_CypAdhd — CYP ADHD Post-Assessment Discussion review page.
 *
 * Thin wrapper: supplies the PAD document structure and the AI-inputs
 * panel groups to the shared CypAdhdReviewDocument. Review of Assessment
 * drafts the summary and the goals. Treatment Options drafts the
 * preference. Health Checks drafts the screening decisions and the
 * outcome. A paragraph with no generated text is hidden, so listing it
 * here is safe before the clinician has reached that page.
 *
 * The two paragraph codes must match the keys in llm_processor_cyp_pad.py
 * (backend source of truth). Paragraphs without generated content (e.g.
 * goals when the outcome is negative) are hidden and excluded from the
 * approval denominator by the shared component/hook, so listing both here
 * is always safe.
 *
 * The 'padReportApproved' flag this page's exit writes completes the
 * uniform four-section gate for Patient Report auto-finalisation.
 */

// Section titles and codes must match the backend processor keys.
const PAD_DOC_STRUCTURE = {
  'Review of Assessment': {
    1: { title: 'Summary of Assessment Findings', code: 'cyp_pad_REV_summary_of_findings' },
    2: { title: "Patient's Goals", code: 'cyp_pad_REV_patient_goals' },
  },
  'Treatment Options': {
    1: { title: 'Treatment preference', code: 'cyp_pad_TRT_patient_preferences' },
  },
  'Health Checks': {
    1: { title: 'Screening decisions', code: 'cyp_pad_HLT_screening_decisions' },
    2: { title: 'Outcome of health checks', code: 'cyp_pad_HLT_outcome' },
  },
};

// The CYP PAD structured-entry buckets (see llm_processor_cyp_pad.py):
// Clinician Decisions carries the outcome and discussed flags; Clinician
// Notes carries the reflection and goal notes; Patient Prefill carries
// the parent/carer's pre-assessment referral goals.
const PAD_INPUT_PANEL_GROUPS = [
  ['Your decisions', 'Clinician Decisions'],
  ['Your notes', 'Clinician Notes'],
  ["Parent/carer's report", 'Patient Prefill'],
];

export default function RD_PAD_CypAdhd() {
  const buildDocStructure = useCallback(() => PAD_DOC_STRUCTURE, []);
  return (
    <CypAdhdReviewDocument
      stageKey="postAssessmentDiscussion"
      flagPrefix="pad"
      moduleTitle="Post-Assessment Discussion"
      editStageCode="PAD"
      buildDocStructure={buildDocStructure}
      inputPanelGroups={PAD_INPUT_PANEL_GROUPS}
    />
  );
}

export { PAD_DOC_STRUCTURE };
